import { AlertCircle, IdCard, Loader2, Percent } from 'lucide-react'
import { useConfigForm } from './useSingleConfig'
import { CurrencyInput, Field, NumberInput, PageHead, SaveBar, SectionCard, StateChip, SwitchRow } from './ui'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const centsToEuros = (c: unknown): string => {
  const n = Number(c)
  return Number.isFinite(n) && n !== 0 ? (n / 100).toFixed(2) : n === 0 ? '0.00' : ''
}
const eurosToCents = (e: string): number => {
  const n = parseFloat(String(e).replace(',', '.'))
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

type Form = { firstDayRate: string; discount: string; enabled: boolean }

export function PcardConfigPage() {
  const { query, form, dirty, patch, reset, commit, saving, ok, error } = useConfigForm<Form>(
    'pcardConfiguration',
    (el) => ({
      firstDayRate: centsToEuros(el.firstDayRate),
      discount: el.discount == null || el.discount === '' ? '' : String(el.discount),
      enabled: isYes(el.enabled),
    }),
  )

  function save() {
    commit((f) => ({
      firstDayRate: eurosToCents(f.firstDayRate),
      discount: Number(f.discount) || 0,
      enabled: f.enabled,
    }))
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Card Parking"
        description="Tariffa agevolata e sconto riservati ai titolari di Card Parking (clienti frequenti, residenti, pendolari)."
      />

      {query.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la configurazione.
        </div>
      ) : !form ? (
        <div className="cv-panel flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="mx-auto max-w-2xl">
            <SectionCard
              title="Programma Card Parking"
              icon={IdCard}
              desc="Condizioni economiche applicate agli utenti Card Parking."
              aside={<StateChip on={form.enabled} />}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Tariffa primo giorno" hint="Importo dedicato agli utenti Card Parking per il primo giorno.">
                    <CurrencyInput value={form.firstDayRate} disabled={saving} onChange={(v) => patch({ firstDayRate: v })} />
                  </Field>
                  <Field label="Percentuale di sconto" hint="Sconto applicato alle soste dei titolari di card.">
                    <NumberInput
                      value={form.discount}
                      min={0}
                      step={1}
                      suffix="%"
                      disabled={saving}
                      onChange={(v) => patch({ discount: v })}
                    />
                  </Field>
                </div>
                <div className="border-t border-line-soft pt-1">
                  <SwitchRow
                    title="Attiva il programma Card Parking"
                    desc="Se disattivo, le condizioni agevolate non vengono applicate in fase di acquisto."
                    on={form.enabled}
                    disabled={saving}
                    onToggle={(v) => patch({ enabled: v })}
                  />
                </div>
              </div>
            </SectionCard>

            <div className="mt-3 flex items-start gap-2.5 px-1 text-[12.5px] text-muted">
              <Percent className="mt-0.5 h-4 w-4 shrink-0 text-faint" />
              <span>Le tessere e i singoli titolari si gestiscono nel gestionale; qui si imposta solo la regola economica.</span>
            </div>
          </div>

          <SaveBar dirty={dirty} saving={saving} error={error} ok={ok} onSave={save} onReset={reset} />
        </>
      )}
    </div>
  )
}
